import { styleConstants } from '@paywithextend/components'
import React from 'react'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import BudgetDetailsBox from 'src/components/BudgetDetailsBox/BudgetDetailsBox'
import BudgetSummaryBox from 'src/components/BudgetSummaryBox/BudgetSummaryBox'
import BudgetSummaryInactiveBox from 'src/components/BudgetSummaryInactiveBox/BudgetSummaryInactiveBox'
import BudgetUsersBox from 'src/components/BudgetUsersBox/BudgetUsersBox'
import BudgetVirtualCardsBox from 'src/components/BudgetVirtualCardsBox/BudgetVirtualCardsBox'
import HorizontalContentCollection from 'src/components/HorizontalContentCollection/HorizontalContentCollection'
import NotesBox from 'src/components/NotesBox/NotesBox'
import VerticalContentCollection from 'src/components/VerticalContentCollection/VerticalContentCollection'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import {
  CreditCardPermissions,
  CreditCardPermissionsModel,
} from 'src/models/creditCardPermissions.models'
import { UserListModel } from 'src/models/user.models'
import { VirtualCardListModel } from 'src/models/virtualCard.models'

interface BudgetOverviewBackgroundPageContentProps {
  className?: string
  creditCard: CreditCardListModel
  activeVcnCount?: number
  vcnCount?: number
  creditCardPermissions?: CreditCardPermissionsModel
  activeCreditCardVirtualCards: VirtualCardListModel[]
  virtualCardsLoading: boolean
  virtualCardsComplete: boolean
  creditCardOwner: UserListModel | undefined
  creditCardUser: UserListModel | undefined
  isBudgetRecipient: boolean
  parentCreditCardPermissions?: CreditCardPermissionsModel
  parentCreditCard?: CreditCardListModel
}

const BudgetOverviewBackgroundPageContent: React.FC<
  BudgetOverviewBackgroundPageContentProps
> = (props): React.ReactElement => {
  const translate = useTranslate()

  const canViewParentCard =
    props.parentCreditCardPermissions?.permissions.includes(
      CreditCardPermissions.Read,
    )
  return (
    <BackgroundPageContent>
      <HorizontalContentCollection splitLeft>
        {props.creditCard.isActive ? (
          <BudgetSummaryBox creditCard={props.creditCard} />
        ) : (
          <BudgetSummaryInactiveBox creditCard={props.creditCard} />
        )}
        <BudgetVirtualCardsBox
          creditCard={props.creditCard}
          vcnCount={props.activeVcnCount || 0}
        />
      </HorizontalContentCollection>
      <HorizontalContentCollection splitLeft>
        <VerticalContentCollection>
          <BudgetDetailsBox
            isInactive={
              props.creditCard.isInactive || props.creditCard.isClosed
            }
            owner={props.creditCardOwner}
            recipient={props.creditCardUser}
            fundingSource={
              props.creditCard.parentCreditCardDisplayName !== undefined
                ? props.creditCard.parentCreditCardDisplayName
                : ''
            }
            inactiveSince={
              props.creditCard.inactiveSinceTimezonedString !== undefined
                ? props.creditCard.inactiveSinceTimezonedString
                : ''
            }
            activeUntil={props.creditCard.activeUntilTimezonedString}
            createdAt={props.creditCard.createdAtTimezonedString}
            canViewParent={!!canViewParentCard}
            parentId={props.creditCard.parentCreditCardId}
            last4={
              props.parentCreditCard?.last4 !== undefined
                ? props.parentCreditCard.last4
                : ''
            }
            recurrenceDetails={
              props.creditCard.recurrence !== undefined
                ? {
                    timezone: props.creditCard.timezone,
                    recurrence: props.creditCard.recurrence,
                    currency: props.creditCard.currency,
                  }
                : undefined
            }
          />
          <NotesBox
            backgroundColor={styleConstants.colors.white}
            copy={translate(
              "When you create or update a budget, leave a memo about what it's for and your note will appear here.",
            )}
            notes={props.creditCard.notes}
          />
        </VerticalContentCollection>
        <BudgetUsersBox
          creditCard={props.creditCard}
          budgetPermissions={props.creditCardPermissions}
        />
      </HorizontalContentCollection>
    </BackgroundPageContent>
  )
}

export default BudgetOverviewBackgroundPageContent
